import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  fetchSpecialtiesRequest,
  selectSpecialties,
} from "../store/reducers/specialtySlice";

/** A specialty as the API embeds it in a facility or doctor payload. */
export type EmbeddedSpecialty =
  | string
  | { id?: string | number; code?: string; label?: string };

/**
 * Resolves an embedded specialty to a label in the UI's language.
 *
 * MED-1462 / MED-1523: the specialty list embedded in a facility or doctor
 * payload is NOT localized — neither `GET /healthcare-facilities/{id}` nor the
 * admin's `GET /admin/doctors[/{id}]` takes a `lang`, so those labels come back
 * English on a French UI. The specialty store IS language-aware (it is
 * refetched on every language change, by this hook), so resolve against it
 * FIRST and keep the embedded label only as the fallback for a specialty the
 * store does not know.
 *
 * Fixing this properly means threading `lang` through the API's doctor and
 * facility responses; until then this is the admin-side workaround.
 */
export function useSpecialtyLabels(): (specialty: EmbeddedSpecialty) => string {
  const dispatch = useAppDispatch();
  const { i18n } = useTranslation();
  const storeSpecialties = useAppSelector(selectSpecialties);

  useEffect(() => {
    dispatch(fetchSpecialtiesRequest({ lang: i18n.language }));
    const handleLanguageChange = (lang: string) => {
      dispatch(fetchSpecialtiesRequest({ lang }));
    };
    i18n.on("languageChanged", handleLanguageChange);
    return () => {
      i18n.off("languageChanged", handleLanguageChange);
    };
  }, [dispatch, i18n]);

  return useCallback(
    (specialty: EmbeddedSpecialty): string => {
      if (typeof specialty === "string") return specialty;
      const fromStore = storeSpecialties.find(
        s =>
          (specialty.id !== undefined &&
            String(s.id) === String(specialty.id)) ||
          (specialty.code !== undefined && s.code === specialty.code)
      );
      return (
        fromStore?.label?.trim() ||
        specialty.label?.trim() ||
        specialty.code ||
        ""
      );
    },
    [storeSpecialties]
  );
}
